import { Button } from 'components/Button/Button';
import { OverlayModal } from 'components/OverlayModal/OverlayModal';
import { OverlayModalBody } from 'components/OverlayModal/OverlayModalBody';
import { OverlayModalFooter } from 'components/OverlayModal/OverlayModalFooter';
import { OverlayModalHeader } from 'components/OverlayModal/OverlayModalHeader';
import { useLocale } from 'containers/App/LocaleContext';
import type { FormikProps } from 'formik';
import { useRef } from 'react';
import type { FC } from 'react';
import { TestForm, type TestValues } from './TestForm';
import {
  type EventType,
  type WebHookAction,
  type WebHookRowData,
  type WebHookType,
  webHookTypes,
} from './WebHooksState';
import type { TestWebHookCommand } from './commands/testWebHook';
import { WEBHOOK_STATUSES } from './constants';
import { getSelectedEventTypes } from './helpers/getInitialValues';

type Props = {
  eventTypes: EventType[];
  onClose: () => void;
  testWebHook: (command: TestWebHookCommand) => void;
  webhook: WebHookRowData;
};

const getActionsByType = (eventTypes: EventType[]) => {
  const actionsByType: Record<WebHookType, WebHookAction[]> = {} as Record<
    WebHookType,
    WebHookAction[]
  >;

  for (const {
    type,
    description: { actions },
  } of eventTypes) {
    actionsByType[type] = [...actions];
  }

  return actionsByType;
};

// Record one testable (type/action) option: add the action to its type's list
// and merge the condition's status(es) into the per-(type/action) status list.
const addActionOption = (
  type: WebHookType,
  action: WebHookAction,
  status: string | undefined,
  typeActions: WebHookAction[],
  statusesByKey: Record<string, string[]>,
) => {
  if (!typeActions.includes(action)) {
    typeActions.push(action);
  }
  const mapKey = `${type}/${action}`;
  const list = statusesByKey[mapKey] ?? [];
  statusesByKey[mapKey] = list;
  // A missing status matches every status; expand to the full set.
  for (const status_ of status ? [status] : [...WEBHOOK_STATUSES]) {
    if (!list.includes(status_)) {
      list.push(status_);
    }
  }
};

// Fine-grained webhooks store their events in `trigger` (with empty `types`).
// Build the per-type action list and a per-(type/action) status list from those
// conditions so the user can test any configured status.
const getFineGrainedOptions = (
  trigger: NonNullable<WebHookRowData['trigger']>,
  allActions: Record<WebHookType, WebHookAction[]>,
) => {
  const actions = {} as Record<WebHookType, WebHookAction[]>;
  const statusesByKey: Record<string, string[]> = {};

  for (const { type, action, status } of trigger) {
    const typeActions = actions[type] ?? [];
    actions[type] = typeActions;
    // A condition may match a whole type (no action); expand to every action.
    const conditionActions = action
      ? [action as WebHookAction]
      : [...(allActions[type] ?? [])];
    for (const conditionAction of conditionActions) {
      addActionOption(
        type,
        conditionAction,
        status,
        typeActions,
        statusesByKey,
      );
    }
  }

  return { actions, statusesByKey };
};

// Build the testable type/action options for a webhook. Fine-grained webhooks
// store their events in `trigger` (with empty `types`), so deriving options from
// `types`/`sendAll` alone yields an empty list and crashes the form. Always
// returns a non-empty `types` list and an `actions` map covering every type.
const getFormOptions = (webhook: WebHookRowData, eventTypes: EventType[]) => {
  const allActions = getActionsByType(eventTypes);

  // Fine-grained webhooks: derive options from the trigger conditions, including
  // a per-(type/action) status list so the user can test a configured status.
  if (!webhook.sendAll && webhook.trigger?.length) {
    const { actions, statusesByKey } = getFineGrainedOptions(
      webhook.trigger,
      allActions,
    );

    const types = Object.keys(actions) as WebHookType[];
    if (types.length) {
      return {
        actions: { ...allActions, ...actions },
        types,
        withStatus: true,
        statusesByKey,
      };
    }
  }

  // Legacy webhooks (sendAll / types[]). Status is not part of their config.
  const selected = getSelectedEventTypes(webhook);
  const types = webhook.sendAll
    ? [...webHookTypes]
    : webHookTypes.filter((value) => selected[value]);

  return {
    actions: allActions,
    // Never return an empty list — the form indexes types[0] unconditionally.
    types: types.length ? types : [...webHookTypes],
    withStatus: false,
    statusesByKey: {} as Record<string, string[]>,
  };
};

export const TestDialog: FC<Props> = ({
  eventTypes,
  onClose,
  testWebHook,
  webhook,
}) => {
  const msg = useLocale();
  const formOptions = getFormOptions(webhook, eventTypes);

  const formRef = useRef<FormikProps<TestValues>>();

  const submitForm = () => {
    if (formRef.current) {
      formRef.current.submitForm();
    }
  };

  return (
    <OverlayModal onClose={onClose} contentClassName='add-path-verification'>
      <OverlayModalHeader>
        <h4>{msg.settings.webHooks.testHeading}</h4>
      </OverlayModalHeader>
      <OverlayModalBody>
        <TestForm
          options={formOptions}
          webhookId={webhook.id}
          webhookName={webhook.name}
          testWebHook={testWebHook}
          ref={formRef}
        />
      </OverlayModalBody>
      <OverlayModalFooter>
        <div className='flex justify-between'>
          <Button variant='primary' type='button' onClick={submitForm}>
            {msg.settings.webHooks.btnTest}
          </Button>
          <Button variant='secondary' type='button' onClick={onClose}>
            {msg.settings.webHooks.btnClose}
          </Button>
        </div>
      </OverlayModalFooter>
    </OverlayModal>
  );
};
